import {useRef} from 'react';

import {I18n} from '@aws-amplify/core';
import {Alert, SimpleGrid} from '@mantine/core';
import {useNavigate} from 'react-router-dom';

import {useBindingDriftReports} from '../hooks/useBindingDriftReports.ts';
import {useBindingDriftReportsTableColumns} from '../hooks/useBindingDriftReportsTableColumns.tsx';
import {moduleConfig} from '../config';
import {BindingDriftReportSummary} from '../types.ts';

import {
    MantineDataTable,
    DataTableTokenPagination,
} from '@flexinet/ui-components';

// Read-only (FNP-585): no selection / no edit drawer. Row click opens the detail
// view.
//
// No keyword search and no filter drawer, deliberately. The API takes no filter
// argument (the sweep scans the whole platform, one run type), and no column carries
// text a keyword could match: every one is a timestamp, a count or a derived badge.
// Sorting by Run Started or a count column is how an operator finds a run.
export const RecordsTable = () => {
    const {columns} = useBindingDriftReportsTableColumns();
    const navigate = useNavigate();

    const {
        items,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
    } = useBindingDriftReports();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // The BindingDriftReport id is a COMPOSITE key
    // (`binding-drift-report:<runStartedAt-iso>`) containing `:`, `-` and `.`, so it
    // MUST be encodeURIComponent'd before going into the path — otherwise the path
    // would be mangled, useParams().id would come back partial, and
    // getBindingDriftReport(<partial id>) returns nothing → the detail page hangs on
    // its loading spinner forever. react-router v6 useParams() auto-decodes, so the
    // detail page receives the original id. (Same composite-id rule as the sibling
    // observability modules FNP-533/534/535/536.)
    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            {isError && (
                <Alert color='red' title={I18n.get('Could not load binding drift runs')}>
                    {I18n.get('The billing API did not return the sweep history. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            {/* One empty state, not two: with no search or filter there is nothing to
                "not match". Gated on !loading so it does not flash during the first
                fetch, and on !isError so a failed load keeps its own Alert instead of
                claiming there are no runs. */}
            {!loading && !isError && items.length === 0 && (
                <Alert color='blue' title={I18n.get('No binding drift runs recorded yet')}>
                    {I18n.get('The binding drift sweep writes one report per run. None has run in this environment yet, so this is not an error.')}
                </Alert>
            )}

            <MantineDataTable<BindingDriftReportSummary>
                columns={columns}
                records={items}
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) => navigate(`${moduleConfig.basePath}/${encodeURIComponent(record.id)}`)}
            />

            <DataTableTokenPagination
                loadedCount={items.length}
                nextToken={nextToken}
                noun={'run'}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
